import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getCurrentUser } from 'api';
import { Loading, PageWrapper } from 'components';
import {
  queryKeys,
  routes,
  SALON_SITE_URL,
  TEXT_STRING,
} from '../../constants';
import { useMessage, useToast } from 'hooks';
import { useUserStore } from 'store';
import { TUser } from 'types';
import { toast } from 'react-toastify';
import { usePubNub } from 'pubnub-react';

function MessageManagement() {
  const [searchParams] = useSearchParams();
  return searchParams.has('isSalon') ? (
    <SalonSiteRedirect />
  ) : (
    <ManagementChannelNavigation />
  );
}

function SalonSiteRedirect() {
  const attemptedRef = useRef(false);

  useEffect(() => {
    if (attemptedRef.current) return;
    attemptedRef.current = true;
    window.location.replace(SALON_SITE_URL);
  }, []);

  return (
    <PageWrapper className="flex items-center justify-center min-h-screen">
      <Loading />
    </PageWrapper>
  );
}

function ManagementChannelNavigation() {
  const navigate = useNavigate();
  const pubnub = usePubNub();
  const { toastError } = useToast();
  const { isAuthenticated, clientId, user } = useUserStore();
  const { findOrCreateMgmChannelAndNavigate } = useMessage({ pubnub });
  const attemptedRef = useRef(false);

  const { data: currentUser, isSuccess, isError } = useQuery<TUser>({
    queryKey: [queryKeys.CURRENT_USER],
    queryFn: getCurrentUser,
    enabled: isAuthenticated,
    refetchOnMount: false,
  });

  const managementChannelId =
    user?.managementChannelId ?? currentUser?.managementChannelId;

  const isReady = isAuthenticated
    ? isSuccess && !!managementChannelId
    : !!clientId;

  const isFetchFailed = isAuthenticated && isError;
  const isFetchDoneWithoutChannel =
    isAuthenticated && isSuccess && !managementChannelId;

  useEffect(() => {
    if (attemptedRef.current) return;

    if (isFetchFailed || isFetchDoneWithoutChannel) {
      attemptedRef.current = true;
      toast.error(TEXT_STRING.ERROR.MGM_CHANNEL_NOT_FOUND);
      navigate(routes.MESSAGE, { replace: true });
      return;
    }

    if (!isReady) return;

    attemptedRef.current = true;
    let cancelled = false;

    const openManagementChannel = async () => {
      if (isAuthenticated && managementChannelId) {
        navigate(routes.CONVERSATION.replace(':id', managementChannelId), {
          replace: true,
        });
        return;
      }

      try {
        const channelId = await findOrCreateMgmChannelAndNavigate();
        if (!cancelled && !channelId) {
          navigate(routes.MESSAGE, { replace: true });
        }
      } catch (error) {
        if (!cancelled) {
          toastError(error instanceof Error ? error : new Error(String(error)));
          navigate(routes.MESSAGE, { replace: true });
        }
      }
    };

    openManagementChannel();

    return () => {
      cancelled = true;
    };
  }, [
    clientId,
    findOrCreateMgmChannelAndNavigate,
    isAuthenticated,
    isFetchDoneWithoutChannel,
    isFetchFailed,
    isReady,
    managementChannelId,
    navigate,
    toastError,
  ]);

  return (
    <PageWrapper className="flex items-center justify-center min-h-screen">
      <Loading />
    </PageWrapper>
  );
}

export default MessageManagement;
